import { useEffect, useRef, useState } from "react";
import { StepContainer } from "../components/StepContainer";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceFields, type VoiceFieldsValues } from "../components/fields/VoiceFields";
import { useUpdateBusinessContext } from "../hooks/useUpdateBusinessContext";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { STEPS, findStepIndexBySlug } from "../config/steps";

interface Values extends VoiceFieldsValues {
  ethical_constraints: string;
}

export interface VoiceStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function VoiceStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: VoiceStepProps) {
  const step = STEPS[findStepIndexBySlug("voz")];
  const { mutate, mutateDebounced } = useUpdateBusinessContext();
  const { query } = useBusinessContext();

  const [values, setValues] = useState<Values>({
    tone_formality: 5,
    banned_words: [],
    anchor_words: [],
    example_phrases: [],
    ethical_constraints: "",
  });
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    const row = query.data;
    if (!row) return;
    setValues({
      tone_formality: row.tone_formality ?? 5,
      banned_words: row.banned_words ?? [],
      anchor_words: row.anchor_words ?? [],
      example_phrases: row.example_phrases ?? [],
      ethical_constraints: row.ethical_constraints ?? "",
    });
  }, [query.data]);

  useEffect(() => {
    const ok =
      values.banned_words.length + values.anchor_words.length >= 3 &&
      values.example_phrases.length >= 1;
    onValidityChange(ok);
    if (onMissingChange) {
      const missing: string[] = [];
      if (values.banned_words.length + values.anchor_words.length < 3) {
        missing.push("3+ palavras (banidas/âncora)");
      }
      if (values.example_phrases.length < 1) missing.push("frase de exemplo");
      onMissingChange(missing);
    }
  }, [values, onValidityChange, onMissingChange]);

  useEffect(() => {
    onAttemptContinue(async () => {
      const v = valuesRef.current;
      const ok =
        v.banned_words.length + v.anchor_words.length >= 3 && v.example_phrases.length >= 1;
      if (!ok) return false;
      await mutate(v);
      return true;
    });
  }, [mutate, onAttemptContinue]);

  const patch = (p: Partial<Values>) => {
    setValues((prev) => {
      const next = { ...prev, ...p };
      mutateDebounced(p as never);
      return next;
    });
  };

  return (
    <StepContainer
      icon={step.icon}
      headline={step.headline}
      sub={step.sub}
      importanceNote="A IA replica seu tom — palavras banidas + âncoras evitam frases fora do seu jeito."
    >
      <div className="flex flex-col gap-6">
        <VoiceFields value={values} onChange={patch} />

        <div className="space-y-1.5">
          <Label htmlFor="ethical">Restrições éticas (opcional)</Label>
          <Textarea
            id="ethical"
            value={values.ethical_constraints}
            onChange={(e) => patch({ ethical_constraints: e.target.value })}
            placeholder="Ex.: nunca prometer cura, não usar urgência fake, não comparar competidores."
            rows={3}
            className="resize-none"
          />
        </div>
      </div>
    </StepContainer>
  );
}
